import _ from 'lodash'
import { createSelector } from 'reselect'
import { getMultiStart } from './multiStart/selectors'
import { getPinned } from './pinned/selectors'
import { getProfiles } from './profiles/selectors'
import { getTokens } from './tokens/selectors'
import { getOrder } from './order/selectors'

// What I want in localforage is something like this:
// list: {
//     tq: {
//         testuser_1: {
//             usename: 'testuser_1',
//             hash: '',
//             multiLaunch: true,
//             profile: 'Default',
//             remember: true,
//             tokens: {
//                 accessToken: '',
//                 refreshToken: '',
//             },
//         },
//         testuser_2: {
//             usename: 'testuser_2',
//             profile: 'Default',
//             shell: true,
//         },
//     },
// }
// order: {
//     tq: ['testuser_1'],
// }

// todo: to begin with support this so that it works with current localforage
//       but later, simplify it and make sure it is backwards compatible
// We could for example remove the pinned status from localforage, since it is
// by definition only pinned accounts there (just remember profile shells...)

// Start by getting all pinned users from state
// And populate all the required fields
const getPinnedUsersForStorage = createSelector(
    [getPinned, getMultiStart, getProfiles, getTokens],
    // pinned: { tq: ['testuser_1'] }
    // multiStart: { tq: ['testuser_1'] }
    // profiles: { tq: { testuser_1: 'Default' } }
    // tokens: { tq: { testuser_1: { access: '', refresh: '' } } }
    (pinned, multiStart, profiles, tokens) =>
        _.reduce(
            pinned,
            (pinnedUsers, users, serverId) => {
                // If the users list is not empty then add it
                if (users != null && users.length > 0) {
                    // Now we now that one or more users are logged in and pinned
                    // We want to reduce that users array to a new users object
                    pinnedUsers[serverId] = users.reduce((res, userId) => {
                        // start by creating the object
                        const user = {
                            username: userId,
                            remember: true,
                        }
                        // Add the user hash
                        user.hash = '' // todo: remove this line
                        // Add multilaunch
                        user.multiLaunch = (multiStart[serverId] || []).indexOf(userId) > -1
                        // Add profile
                        user.profile = (profiles[serverId] || {})[userId] || 'Default'
                        // Finally add tokens
                        const userTokens = (tokens[serverId] || {})[userId] || {
                            access: '',
                            refresh: '',
                        }
                        user.tokens = {
                            accessToken: userTokens.access,
                            refreshToken: userTokens.refresh,
                        }

                        // Add to the result
                        res[userId] = user
                        return res
                    }, {})
                }
                return pinnedUsers
            },
            {},
        ),
)

// Create profile shells, so that we can remember what profile belongs to an
// account even between logins
const getProfilesForStorage = createSelector([getProfiles], profiles =>
    // profiles: { tq: { testuser_1: 'Default' } }
    _.reduce(
        profiles,
        (profileShells, users, serverId) => {
            profileShells[serverId] = _.reduce(
                users,
                (shell, profile, userId) => {
                    if (profile != null && profile !== '') {
                        shell[userId] = {
                            username: userId,
                            profile,
                            shell: true,
                        }
                    }
                    return shell
                },
                {},
            )
            return profileShells
        },
        {},
    ),
)

// Combine the pinned users and the shells into a list object
// using the format the localforage expects
// Always use the user from pinnedUsers if it exists,
// profileShells should only be added when the pinnedUser is missing!
const getUserListForStorage = createSelector(
    [getPinnedUsersForStorage, getProfilesForStorage],
    (pinnedUsers, profileShells) => {
        // pinnedUsers:   { tq: { testuser_1: {...} } }
        // profileShells: { tq: { testuser_1: {...} } }
        const list = { ...pinnedUsers }
        _.forOwn(profileShells, (users, serverId) => {
            if (list[serverId] == null) {
                // No pinned users for this server, we can just use the
                // profiles shells object directly
                list[serverId] = { ...users }
            } else {
                // For each user, only add it if it is missing
                _.forOwn(users, (user, userId) => {
                    if (list[serverId][userId] == null) {
                        list[serverId][userId] = { ...user }
                    }
                })
            }
        })
        return list
    },
)

// Get order for pinned accounts only
const getUserOrderForStorage = createSelector([getPinned, getOrder], (pinned, order) =>
    // pinned: { tq: ['testuser_1'] }
    // order: { tq: ['testuser_1'] }

    // The order from order is important so we reduce that one
    _.reduce(
        order,
        (pinnedOrder, userIds, serverId) => {
            // If either list is empty, we are done
            if (userIds == null || userIds.length < 1) return pinnedOrder
            if (pinned[serverId] == null || pinned[serverId].length < 1) return pinnedOrder

            // Since both lists contain something, move on
            pinnedOrder[serverId] = []
            userIds.forEach((userId) => {
                if (pinned[serverId].indexOf(userId) > -1) {
                    pinnedOrder[serverId].push(userId)
                }
            })
            return pinnedOrder
        },
        {},
    ),
)

// eslint-disable-next-line
export const getOldUsersForStorage = createSelector(
    [getUserListForStorage, getUserOrderForStorage],
    (list, order) => ({ list, order }),
)



// WEBPACK FOOTER //
// ./src/features/Users/data/storageSelectors.js